import { Input } from '../../../Input';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconArrowsUpDown,
  IconColumns3,
  IconFilter,
  IconFilterOff,
  IconSearch,
} from '@tabler/icons-react';
import { Combobox } from '../../../Combobox';
import {
  Dialog,
  DialogActions,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '../../../Dialog';
import { Badge } from '../../../Badge';
import { Button } from '../../../Button';
import { CellFilter } from './CellFilter';
import { Popover, PopoverContent, PopoverTrigger } from '../../../Popover';
import { Tooltip } from '../../../Tooltip';
import { ToggleWithLabel } from '../../../ToggleWithLabel';
import { useTableContext } from '../../hooks/useTableContext';
import { ReactNode, useMemo } from 'react';
import useI18n from '../../../../hooks/useI18n';

type FilterProps = {
  children?: ReactNode;
  dialogAriaDescribedby?: string;
  disableFullscreen?: boolean;
};

export function Filter<TData>({
  children,
  dialogAriaDescribedby,
  disableFullscreen = false,
}: FilterProps) {
  const { t } = useI18n();
  const sortByLabel = t('sortBy');
  const hideShowColumnsLabel = t('hideShowColumns');
  const {
    isFullscreen,
    setIsFullscreen,
    globalFilter,
    setGlobalFilter,
    table,
    tableState,
  } = useTableContext<TData>();

  const columns = table.getAllLeafColumns();
  const headerColumns = useMemo(() => {
    return columns.filter(
      (column): column is typeof column & { columnDef: { header: string } } =>
        typeof column.columnDef.header === 'string',
    );
  }, [columns]);

  const visibilityColumns = useMemo(() => {
    return headerColumns.filter(
      (column) => column.columnDef?.enableHiding !== false,
    );
  }, [headerColumns]);
  const sortingColumns = useMemo(() => {
    return headerColumns.filter(
      (column) => column.columnDef?.enableSorting !== false,
    );
  }, [headerColumns]);
  const sortingItems = useMemo(() => {
    return sortingColumns.map((column) => ({
      value: column.id,
      label: column.columnDef.header,
    }));
  }, [sortingColumns]);

  const disableHideAll = useMemo(() => {
    return (
      visibilityColumns.filter(
        (column) => column.getCanHide() && column.getIsVisible(),
      ).length === 0
    );
  }, [visibilityColumns]);

  const hasActiveFilters = useMemo(
    () => Boolean(tableState.columnFilters?.length),
    [tableState.columnFilters],
  );

  return (
    <div className='eye:my-24 eye:flex eye:flex-wrap eye:gap-l eye:gap-y-12'>
      <div className='eye:flex eye:flex-1 eye:flex-wrap eye:gap-16'>
        <div className='eye:min-w-[315px] eye:grow eye:lg:max-w-[400px]'>
          <Input
            labelProps={{
              label: t('search') + ':',
              leftSlot: <IconSearch aria-hidden={true} />,
            }}
            showClearButton={true}
            placeholder={t('searchTable')}
            value={globalFilter ?? ''}
            onChange={(e) => setGlobalFilter(e.target.value)}
          />
        </div>
        <div className='eye:grow eye:lg:max-w-[315px]'>
          <Combobox
            fullWidth
            labelProps={{
              float: 'left',
              label: sortByLabel + ':',
              leftSlot: <IconArrowsUpDown aria-hidden />,
            }}
            placeholder={sortByLabel}
            items={sortingItems}
            shouldFilter={false}
            value={tableState.sorting?.[0]?.id || ''}
            onSelect={(item) => {
              table.setSorting([{ id: item, desc: false }]);
            }}
          />
        </div>
        {children && (
          <div className='eye:flex eye:max-h-screen'>{children}</div>
        )}
      </div>
      <div className='eye:flex eye:justify-end eye:gap-xs'>
        <div className='eye:mt-5xs eye:flex eye:items-center eye:justify-end eye:self-start'>
          <Dialog>
            <DialogTrigger asChild className={'eye:mr-4xs'}>
              <Button
                variant='ghost'
                size='icon'
                className='eye:gap-4xs eye:px-2xs'
              >
                <Badge badgeContent={tableState.columnFilters.length}>
                  <span className='eye:flex eye:items-center eye:gap-4xs'>
                    <IconFilter aria-hidden={true} />
                    {t('filter')}
                  </span>
                </Badge>
              </Button>
            </DialogTrigger>
            <DialogContent
              aria-describedby={dialogAriaDescribedby}
              className='eye:max-w-[525px]'
            >
              <DialogTitle>{t('filterTable')}</DialogTitle>
              <CellFilter table={table} columns={headerColumns} />
              <DialogActions>
                <Button
                  variant='ghost'
                  disabled={!hasActiveFilters}
                  onClick={() => table.resetColumnFilters(true)}
                >
                  <IconFilterOff />
                  {t('resetFilters')}
                </Button>
                <DialogClose asChild>
                  <Button variant='outline'>{t('close')}</Button>
                </DialogClose>
              </DialogActions>
            </DialogContent>
          </Dialog>

          <Popover>
            <PopoverTrigger asChild={true}>
              <Tooltip content={hideShowColumnsLabel} asChild={true}>
                <Button
                  size='icon'
                  variant='ghost'
                  aria-label={hideShowColumnsLabel}
                >
                  <IconColumns3 aria-hidden={true} />
                </Button>
              </Tooltip>
            </PopoverTrigger>
            <PopoverContent
              avoidCollisions={false}
              matchTriggerWidth={false}
              side='bottom'
              align='end'
            >
              <div className='eye:text-sm eye:flex eye:max-h-[400px] eye:flex-col eye:gap-12 eye:p-8'>
                <div className={'eye:mb-12 eye:flex eye:gap-8'}>
                  <Button
                    disabled={disableHideAll}
                    variant={'outline'}
                    size={'sm'}
                    onClick={() => table.toggleAllColumnsVisible(false)}
                  >
                    {t('hideAll')}
                  </Button>
                  <Button
                    disabled={table.getIsAllColumnsVisible()}
                    variant={'outline'}
                    size={'sm'}
                    onClick={() => table.toggleAllColumnsVisible(true)}
                  >
                    {t('showAll')}
                  </Button>
                </div>
                {visibilityColumns.map((column) => (
                  <ToggleWithLabel
                    disabled={!column.getCanHide()}
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(checked) =>
                      column.toggleVisibility(checked)
                    }
                    label={column.columnDef.header}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
          {!disableFullscreen && (
            <Tooltip
              content={
                isFullscreen
                  ? t('deactivateFullScreen')
                  : t('activateFullScreen')
              }
              asChild
            >
              <Button
                variant={'ghost'}
                size={'icon'}
                aria-label={
                  isFullscreen
                    ? t('deactivateFullScreen')
                    : t('activateFullScreen')
                }
                onClick={() => setIsFullscreen((prev) => !prev)}
              >
                {isFullscreen ? (
                  <IconArrowsMinimize aria-hidden={true} />
                ) : (
                  <IconArrowsMaximize aria-hidden={true} />
                )}
              </Button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

Filter.displayName = 'Table.Filter';
